import { InvoiceRow, InvoiceFilter, KPISummary, MonthlyNettoData, SupplierNettoData } from '@/types'
import { normalizeStatus, isOverdue, isDueSoon } from '@/utils/invoiceStatus'

/**
 * Filter invoices based on filter criteria
 */
export function filterInvoices(invoices: InvoiceRow[], filter: InvoiceFilter): InvoiceRow[] {
  return invoices.filter(invoice => {
    // Supplier filter
    if (filter.supplierNames.length > 0) {
      if (!filter.supplierNames.includes(invoice.supplierName)) {
        return false
      }
    }

    // Date range filter
    if (filter.dateRange.start && invoice.dateInvoice) {
      if (invoice.dateInvoice < filter.dateRange.start) {
        return false
      }
    }
    if (filter.dateRange.end && invoice.dateInvoice) {
      if (invoice.dateInvoice > filter.dateRange.end) {
        return false
      }
    }

    // Brutto range filter
    if (filter.bruttoRange.min !== null && invoice.brutto !== null) {
      if (invoice.brutto < filter.bruttoRange.min) {
        return false
      }
    }
    if (filter.bruttoRange.max !== null && invoice.brutto !== null) {
      if (invoice.brutto > filter.bruttoRange.max) {
        return false
      }
    }

    // Netto range filter
    if (filter.nettoRange.min !== null && invoice.netto !== null) {
      if (invoice.netto < filter.nettoRange.min) {
        return false
      }
    }
    if (filter.nettoRange.max !== null && invoice.netto !== null) {
      if (invoice.netto > filter.nettoRange.max) {
        return false
      }
    }

    // Rechnungsnummer search
    if (filter.rechnungsnummerSearch) {
      const searchTerm = filter.rechnungsnummerSearch.toLowerCase()
      if (!invoice.rechnungsnummer.toLowerCase().includes(searchTerm)) {
        return false
      }
    }

    // Comment search
    if (filter.commentSearch) {
      const searchTerm = filter.commentSearch.toLowerCase()
      if (!invoice.comment.toLowerCase().includes(searchTerm)) {
        return false
      }
    }

    return true
  })
}

/**
 * Calculate KPI summary
 */
export function calculateKPIs(invoices: InvoiceRow[]): KPISummary {
  const totalBrutto = invoices.reduce((sum, inv) => sum + (inv.brutto ?? 0), 0)
  const totalNetto = invoices.reduce((sum, inv) => sum + (inv.netto ?? 0), 0)
  const totalVatAmount = invoices.reduce((sum, inv) => sum + (inv.vatAmount ?? 0), 0)
  const totalTaxFreeAmount = invoices.reduce((sum, inv) => sum + (inv.taxFreeAmount ?? 0), 0)
  const totalTaxableNetAmount = invoices.reduce((sum, inv) => sum + (inv.taxableNetAmount ?? 0), 0)
  const invoiceCount = invoices.length

  const uniqueSuppliers = new Set(invoices.map(inv => inv.supplierName).filter(Boolean))
  const supplierCount = uniqueSuppliers.size

  // Status / approval KPIs
  let outstandingAmount = 0
  let outstandingCount = 0
  let overdueAmount = 0
  let overdueCount = 0
  let dueSoonAmount = 0
  let dueSoonCount = 0
  let approvedCount = 0
  let pendingCount = 0

  invoices.forEach(inv => {
    const status = normalizeStatus(inv.status)
    const amount = inv.brutto ?? 0

    if (status !== 'paid') {
      outstandingAmount += amount
      outstandingCount += 1
    }
    if (status === 'approved') {
      approvedCount += 1
    }
    if (status === 'open' || status === 'in_review') {
      pendingCount += 1
    }
    if (isOverdue(inv.dueDate, status, inv.documentType)) {
      overdueAmount += amount
      overdueCount += 1
    } else if (isDueSoon(inv.dueDate, status, inv.documentType)) {
      dueSoonAmount += amount
      dueSoonCount += 1
    }
  })

  return {
    totalBrutto,
    totalNetto,
    totalVatAmount,
    totalTaxFreeAmount,
    totalTaxableNetAmount,
    invoiceCount,
    supplierCount,
    outstandingAmount,
    outstandingCount,
    overdueAmount,
    overdueCount,
    dueSoonAmount,
    dueSoonCount,
    approvedCount,
    pendingCount,
  }
}

/**
 * Group invoices by month for time series chart
 */
export function groupByMonth(invoices: InvoiceRow[]): MonthlyNettoData[] {
  const monthMap = new Map<string, MonthlyNettoData>()

  invoices.forEach(invoice => {
    if (!invoice.dateInvoice || invoice.netto === null) {
      return
    }

    const year = invoice.dateInvoice.getFullYear()
    const month = invoice.dateInvoice.getMonth()
    const key = `${year}-${month}`

    const existing = monthMap.get(key)
    if (existing) {
      existing.netto += invoice.netto
    } else {
      monthMap.set(key, {
        month: getMonthName(month, year),
        netto: invoice.netto,
        year,
        monthIndex: month,
      })
    }
  })

  // Sort by date
  return Array.from(monthMap.values()).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year
    return a.monthIndex - b.monthIndex
  })
}

/**
 * Get top suppliers by NETTO
 */
export function getTopSuppliers(invoices: InvoiceRow[], limit = 10): SupplierNettoData[] {
  const supplierMap = new Map<string, number>()

  invoices.forEach(invoice => {
    if (invoice.netto === null) {
      return
    }

    const existing = supplierMap.get(invoice.supplierName) ?? 0
    supplierMap.set(invoice.supplierName, existing + invoice.netto)
  })

  return Array.from(supplierMap.entries())
    .map(([supplierName, netto]) => ({ supplierName, netto }))
    .sort((a, b) => b.netto - a.netto)
    .slice(0, limit)
}

/**
 * Get all unique supplier names
 */
export function getSupplierNames(invoices: InvoiceRow[]): string[] {
  const names = new Set(invoices.map(inv => inv.supplierName).filter(Boolean))
  return Array.from(names).sort()
}

/**
 * Get min/max values for filters
 */
export function getValueRanges(invoices: InvoiceRow[]): {
  brutto: { min: number; max: number }
  netto: { min: number; max: number }
  date: { min: Date; max: Date }
} {
  const bruttoValues = invoices.map(inv => inv.brutto).filter((v): v is number => v !== null)
  const nettoValues = invoices.map(inv => inv.netto).filter((v): v is number => v !== null)
  const dates = invoices.map(inv => inv.dateInvoice).filter((d): d is Date => d !== null)

  return {
    brutto: {
      min: bruttoValues.length > 0 ? Math.min(...bruttoValues) : 0,
      max: bruttoValues.length > 0 ? Math.max(...bruttoValues) : 0,
    },
    netto: {
      min: nettoValues.length > 0 ? Math.min(...nettoValues) : 0,
      max: nettoValues.length > 0 ? Math.max(...nettoValues) : 0,
    },
    date: {
      min: dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : new Date(),
      max: dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : new Date(),
    },
  }
}

/**
 * Helper: Get month name
 */
function getMonthName(monthIndex: number, year: number): string {
  const date = new Date(year, monthIndex, 1)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: '2-digit',
  }).format(date)
}
